import { useCallback } from 'react';
import { shallowEqual } from 'react-redux';

import { Flex, Spacer } from '@zillow/constellation';
import { Accordion, Checkbox, Paragraph, Text, Textarea } from '@zillow/constellation-10';

import stringHelper from '../../../../utils/stringHelper';
import { useSelector } from '../../../../selectors/useSelector';

import DeactivationModalCSEvents, {
  TRIGGER_SOURCE_MAP,
} from '../../../../model/ClickstreamEvent/DeactivationModalCSEvents';

type Props = {
  leads: Array<{ name: string; isIncluded: boolean }>;
  message: string;
  setMessage: (message: string) => void;
  toggleIncludeLead: (index: number) => void;
};

const NotifyLeads = ({ leads = [], message = '', setMessage = () => {}, toggleIncludeLead = () => {} }: Props) => {
  const { currentProperty, user } = useSelector(
    (state) => ({ currentProperty: state.properties.currentProperty, user: state.user }),
    shallowEqual,
  );

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessage(e.target.value);
    },
    [setMessage],
  );
  const numIncludedLeads = leads.filter(({ isIncluded }) => isIncluded).length;
  return (
    <Spacer marginTop="sm" marginBottom="md">
      <Text textStyle="heading-sm">Notify renters who you haven&apos;t responded to yet?</Text>
      <Paragraph css={{ marginBlock: '400' }}>
        Our records show you haven&apos;t replied to {leads.length} other{' '}
        {stringHelper.pluralizeWord(leads.length, 'renter', 'renters')} who contacted you about this property. Consider
        sending a message to let them know it&apos;s no longer available so they can focus on other listings.
      </Paragraph>
      <Paragraph css={{ marginBlock: '400' }}>Customize your message below:</Paragraph>
      <Textarea onChange={onChange} value={message} rows={8} />
      <Accordion.Root css={{ marginBlock: '400' }}>
        <Accordion.Item value="notify-leads-recipients">
          <Accordion.Header>
            <Text textStyle="body-bold" css={{ color: 'text.subtle' }}>
              Recipients
            </Text>
            <Text>
              {numIncludedLeads} {stringHelper.pluralizeWord(numIncludedLeads, 'person', 'people')}
            </Text>
          </Accordion.Header>
          <Accordion.Panel>
            <Paragraph color="text.subtle" css={{ marginBlock: '400' }}>
              If you already responded to one of these renters, you can remove them from receiving this message.
            </Paragraph>
            {leads.map(({ name, isIncluded }, i) => (
              <Flex display="flex" justifyContent="space-between" alignItems="center" key={`lead-${i}`} marginY="md">
                <Paragraph className="fs-mask">{name}</Paragraph>
                <Checkbox
                  onChange={() => {
                    toggleIncludeLead(i);

                    DeactivationModalCSEvents.DynamicNotifyLeads({
                      user,
                      currentProperty,
                      triggerSource: isIncluded ? TRIGGER_SOURCE_MAP.DELETE : TRIGGER_SOURCE_MAP.ADD,
                    });
                  }}
                  checked={isIncluded}
                  data-eventid={DeactivationModalCSEvents.DynamicNotifyLeads.data_event_id}
                />
              </Flex>
            ))}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion.Root>
    </Spacer>
  );
};

export default NotifyLeads;
